// Berry College: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Berry publishes no Common Data Set (its Institutional Research page
//    lists none, and none is posted elsewhere on berry.edu). The first-year
//    counts come from the U.S. Department of Education's IPEDS Admissions
//    survey for Fall 2024, first-time degree-seeking students (College
//    Navigator, unit 139144, and the ADM2024 complete data file: APPLCN
//    6,544, ADMSSN 4,188, ENRLT 616). Berry's own Statistics page gives no
//    applicant or admit counts.
//  - Berry Admission: First-Year Application Process (the Fall 2027 dates
//    table, required and optional materials) and First-Year FAQs (testing).
//  - Berry Nursing (the direct-admit and sophomore-year pathways).
//  - Berry Scholarships and Aid: Tuition and Fees breakdown, "2026-27
//    UNDERGRADUATE Rates and Fees". Berry publishes tuition, fees and room
//    and board only; it lists no books, transportation or personal costs,
//    so none are shown. Room and board is the traditional residence hall
//    with the Unlimited Meal Plan, which first-year students normally live
//    in and are required to have.
//  - College Scorecard, school id 139144 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=139144#admsns',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Berry Admission, First-Year Application Process',
        url: 'https://www.berry.edu/admission/how-to-apply/first-year.aspx',
        read: '2026-10-01',
    },
    faqs: {
        label: 'Berry Admission, First-Year FAQs',
        url: 'https://www.berry.edu/admission/faq/first-year.aspx',
        read: '2026-10-01',
    },
    nursing: {
        label: 'Berry College, Nursing',
        url: 'https://www.berry.edu/academics/nursing/',
        read: '2026-10-01',
    },
    costs: {
        label: 'Berry Scholarships and Aid, Tuition and Fees breakdown',
        url: 'https://www.berry.edu/scholarships-and-aid/tuition-and-fees-breakdown',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?139144-Berry-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'berry-college',
    kbId: 'berry_college',
    name: 'Berry College',
    shortName: 'Berry',
    location: 'Mount Berry, GA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Berry brand identity page); decoration only.
    branding: {
        primary: '#00205B',
        secondary: '#A2AAAD',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/berry_college_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 6544,
        admitted: 4188,
        enrolled: 616,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Berry publishes no Common Data Set and no first-year acceptance rate by school or major; the federal IPEDS survey reports one rate for the college.',
        source: 'ipeds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Berry has no early decision plan. Its Early Action is not binding, and Berry does not publish how many applied or were admitted early.',
        source: 'firstYear',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Berry, not to a major. One program has its own path:',
        rows: [
            { school: 'Nursing', requirement: 'Academically qualified high school seniors are guaranteed admission to the Nursing Program as entering freshmen (direct admit). Otherwise you take pre-nursing and foundation courses and apply to the program in the second semester of sophomore year.', source: 'nursing' },
        ],
        source: 'nursing',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'Begins December 1', binding: false },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'Begins March 1', binding: false },
        ],
        note: 'Each deadline is for the application and all required materials: the personal essay and official high school transcripts. Completing your file by November 1 makes sure you are considered for Berry\'s full range of scholarships. The deposit deadline is May 1. Berry takes its own application or the Common Application; both are free. FAFSA priority date: January 15.',
        source: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Neither the SAT nor the ACT is required for admission to Berry, though you are welcome to send scores. Berry says sending scores, or not, will not count against you in admission, and scores are not used in awarding its own merit scholarships. Georgia residents seeking the Zell Miller Scholarship need qualifying test scores.',
        source: 'faqs',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 44190 },
            { item: 'Student activity fee', amount: 225 },
            { item: 'Technology fee', amount: 150 },
            { item: 'Housing and food (traditional residence hall, Unlimited Meal Plan)', amount: 14980 },
        ],
        total: 59545,
        note: 'For an undergraduate in a traditional residence hall, where first-year students normally live, with the Unlimited Meal Plan first-year students must have. Berry publishes no estimate for books, transportation or personal costs, so they are not included.',
        source: 'costs',
        netPrice: {
            average: 21964,
            byIncome: [
                { income: '$0 to $30,000', amount: 18307 },
                { income: '$30,001 to $48,000', amount: 14176 },
                { income: '$48,001 to $75,000', amount: 18427 },
                { income: '$75,001 to $110,000', amount: 22177 },
                { income: 'Over $110,000', amount: 29842 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Berry\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
